import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { BsCloudUpload } from "react-icons/bs";
import { MdDeleteForever } from "react-icons/md";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import { useSession } from "../../../../context/SessionContext";
import { filterStoreProjects } from "../../projectFilters";
import { getallWorkOrders } from "../../../../Redux/actions/Operationsactions/getworkorders";
import { TextAreaField } from "../../../Common/FormComponents";
import { getMeasurementBooks, saveMeasurementBook } from "./measurementBookStorage";
import "../../AddContractorsAndVendors.css";

const emptyLine = () => ({
  id: `${Date.now()}-${Math.random()}`,
  Item: "",
  Location: "",
  No: 1,
  Length: "",
  Breadth: "",
  Height: "",
  Unit: "",
});
const numberOrZero = (value) => Number(value) || 0;
const lineQuantity = (line) =>
  numberOrZero(line.No) *
  numberOrZero(line.Length) *
  numberOrZero(line.Breadth) *
  numberOrZero(line.Height);

const MeasurementBookForm = () => {
  const history = useHistory();
  const attachmentInputRef = useRef(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const { rolesdata, session } = useSession();
  const projects = filterStoreProjects(rolesdata?.data?.Projects);
  const workOrders = useSelector((state) => state.getAllWorkOrders?.data || []);
  const initialProjectId = location.state?.ProjectId || "";
  const [lines, setLines] = useState([emptyLine()]);
  const [formData, setFormData] = useState({
    ProjectId: initialProjectId,
    WorkOrderId: "",
    MeasurementDate: new Date().toISOString().slice(0, 10),
    RecordedBy: session?.UserName || session?.Name || "",
    VerifiedBy: "",
    Remarks: "",
    AttachmentName: "",
  });

  useEffect(() => {
    dispatch(getallWorkOrders(formData.ProjectId || undefined));
  }, [dispatch, formData.ProjectId]);

  const recordNumber = useMemo(
    () =>
      `MB-${new Date().getFullYear()}-${String(getMeasurementBooks().length + 1).padStart(4, "0")}`,
    [],
  );
  const selectedWorkOrder = useMemo(
    () =>
      workOrders.find(
        (workOrder) =>
          String(workOrder.WorkOrderId || workOrder.Id) === String(formData.WorkOrderId),
      ),
    [formData.WorkOrderId, workOrders],
  );
  const totalQuantity = useMemo(
    () => lines.reduce((sum, line) => sum + lineQuantity(line), 0),
    [lines],
  );

  const changeForm = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
      ...(name === "ProjectId" ? { WorkOrderId: "" } : {}),
    }));
  };
  const changeAttachment = (event) => {
    const file = event.target.files?.[0];
    setFormData((previous) => ({
      ...previous,
      AttachmentName: file?.name || "",
    }));
  };
  const changeLine = (index, field, value) =>
    setLines((previous) =>
      previous.map((line, lineIndex) => (lineIndex === index ? { ...line, [field]: value } : line)),
    );
  const removeLine = (index) =>
    setLines((previous) =>
      previous.length === 1 ? previous : previous.filter((_, lineIndex) => lineIndex !== index),
    );

  const save = (status) => {
    if (
      !formData.ProjectId ||
      !formData.WorkOrderId ||
      !formData.MeasurementDate ||
      !formData.RecordedBy
    ) {
      toast.error("Please complete all required measurement book details.");
      return;
    }
    if (lines.some((line) => !line.Item || !line.Location || !line.Unit)) {
      toast.error("Please complete the work item, location, and unit for every measurement row.");
      return;
    }
    const project = projects.find((item) => String(item.ProjectId) === String(formData.ProjectId));
    saveMeasurementBook({
      ...formData,
      RecordNumber: recordNumber,
      ProjectName: project?.ProjectName || "",
      WorkOrderNumber: selectedWorkOrder?.WorkOrderNumber || "",
      ContractorName: selectedWorkOrder?.ContractorName || "",
      Lines: lines,
      TotalQuantity: totalQuantity,
      Status: status,
    });
    toast.success(
      status === "Submitted"
        ? "Measurement Book submitted successfully"
        : "Measurement Book saved as draft",
    );
    history.push("/works/measurement-book");
  };

  return (
    <div className="container-fluid px-3 pt-2">
      <div className="page_heading d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            title="Back"
            style={{ cursor: "pointer", marginRight: 12 }}
            onClick={() => history.goBack()}
          />
          <h4 className="m-0">Add Measurement Book (MB) Record</h4>
        </div>
      </div>

      <div
        className="row mt-3 p-3 mx-0"
        style={{
          background: "var(--card-bg, #fff)",
          border: "1px solid var(--card-border-color, #e2e8f0)",
          borderRadius: 6,
        }}
      >
        <div className="col-md-3 mb-3">
          <label className="input_label">
            Project <span className="estaric">*</span>
          </label>
          <select
            className="input_styling"
            name="ProjectId"
            value={formData.ProjectId}
            onChange={changeForm}
            required
          >
            <option value="">Select Project</option>
            {projects.map((project) => (
              <option key={project.ProjectId} value={project.ProjectId}>
                {project.ProjectName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-3">
          <label className="input_label">
            Work Order (WO) <span className="estaric">*</span>
          </label>
          <select
            className="input_styling"
            name="WorkOrderId"
            value={formData.WorkOrderId}
            onChange={changeForm}
            disabled={!formData.ProjectId}
            required
          >
            <option value="">
              {formData.ProjectId ? "Select Work Order" : "Select a project first"}
            </option>
            {workOrders.map((workOrder) => (
              <option
                key={workOrder.WorkOrderId || workOrder.Id}
                value={workOrder.WorkOrderId || workOrder.Id}
              >
                {workOrder.WorkOrderNumber || `WO-${workOrder.WorkOrderId || workOrder.Id}`}
                {workOrder.ContractorName ? ` (${workOrder.ContractorName})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2 mb-3">
          <label className="input_label">
            Measurement Date <span className="estaric">*</span>
          </label>
          <input
            className="input_styling"
            type="date"
            name="MeasurementDate"
            value={formData.MeasurementDate}
            onChange={changeForm}
            required
          />
        </div>
        <div className="col-md-2 mb-3">
          <label className="input_label">MB Record No.</label>
          <input
            className="input_styling"
            value={recordNumber}
            readOnly
            style={{ backgroundColor: "var(--color-surface-hover, #f8fafc)" }}
          />
        </div>
        <div className="col-md-2 mb-3">
          <label className="input_label">
            Recorded By <span className="estaric">*</span>
          </label>
          <input
            className="input_styling"
            name="RecordedBy"
            value={formData.RecordedBy}
            onChange={changeForm}
            required
          />
        </div>
        <div className="col-md-3">
          <label className="input_label">Attended / Verified By</label>
          <input
            className="input_styling"
            name="VerifiedBy"
            value={formData.VerifiedBy}
            onChange={changeForm}
            placeholder="Resident Engineer / PM"
          />
        </div>
      </div>

      <h6 style={{ color: "var(--color-primary, #0b6462)", marginTop: 24 }}>Work Measurements</h6>
      <div className="table-responsive mt-3">
        <table className="table table-bordered table-hover" style={{ fontSize: 13 }}>
          <thead>
            <tr style={{ height: 42 }}>
              <th className="text-center">#</th>
              <th className="text-center">
                WO Item <span className="estaric">*</span>
              </th>
              <th className="text-center">
                Location / Sub-Work <span className="estaric">*</span>
              </th>
              <th className="text-center">No.</th>
              <th className="text-center">Length (L)</th>
              <th className="text-center">Breadth (B)</th>
              <th className="text-center">Height (H)</th>
              <th className="text-center">Total Qty</th>
              <th className="text-center">
                Unit <span className="estaric">*</span>
              </th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={line.id}>
                <td>{index + 1}</td>
                <td>
                  <input
                    className="input_styling"
                    value={line.Item}
                    onChange={(event) => changeLine(index, "Item", event.target.value)}
                    placeholder="BOQ item / activity"
                  />
                </td>
                <td>
                  <input
                    className="input_styling"
                    value={line.Location}
                    onChange={(event) => changeLine(index, "Location", event.target.value)}
                    placeholder="Location"
                  />
                </td>
                {["No", "Length", "Breadth", "Height"].map((field) => (
                  <td key={field}>
                    <input
                      className="input_styling"
                      type="number"
                      min="0"
                      step="any"
                      value={line[field]}
                      onChange={(event) => changeLine(index, field, event.target.value)}
                    />
                  </td>
                ))}
                <td className="text-right font-weight-bold">
                  {lineQuantity(line).toLocaleString()}
                </td>
                <td>
                  <input
                    className="input_styling"
                    value={line.Unit}
                    onChange={(event) => changeLine(index, "Unit", event.target.value)}
                    placeholder="Sft"
                  />
                </td>
                <td className="text-center">
                  <MdDeleteForever
                    title="Remove row"
                    size="1.3em"
                    style={{
                      cursor: lines.length === 1 ? "not-allowed" : "pointer",
                      color: "#dc3545",
                      opacity: lines.length === 1 ? 0.4 : 1,
                    }}
                    onClick={() => removeLine(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn btn-link p-0"
        style={{ color: "var(--color-primary, #0b6462)", fontWeight: 600 }}
        onClick={() => setLines((previous) => [...previous, emptyLine()])}
      >
        + Add WO Item Row
      </button>
      <div className="row mt-3 ">
        <TextAreaField
          label="Remarks / Notes"
          name="Remarks"
          rows={3}
          value={formData.Remarks}
          onChange={changeForm}
          stacked
          wrapperCol="col-lg-4 col-md-6 col-12"
        />

        <div className="col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
          <label className="input_label d-block m-0 mb-2">Attachment (optional)</label>
          <div
            className="document-upload-box"
            role="button"
            tabIndex={0}
            onClick={() => attachmentInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                attachmentInputRef.current?.click();
              }
            }}
          >
            <BsCloudUpload className="document-upload-icon" />
            {formData.AttachmentName ? (
              <div className="document-upload-file">
                <span title={formData.AttachmentName}>{formData.AttachmentName}</span>
                <button
                  type="button"
                  className="document-upload-remove"
                  onClick={(event) => {
                    event.stopPropagation();
                    setFormData((previous) => ({ ...previous, AttachmentName: "" }));
                    if (attachmentInputRef.current) {
                      attachmentInputRef.current.value = "";
                    }
                  }}
                  title="Remove attachment"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="document-upload-copy">
                <strong>Click to upload</strong>
                <span>PDF, image or document</span>
              </div>
            )}
            <input
              type="file"
              ref={attachmentInputRef}
              className="d-none"
              onChange={changeAttachment}
            />
          </div>
        </div>
        <div className="col-lg-4 col-md-6 col-12 " style={{ marginTop: "20px" }}>
          <div
            className="p-3"
            style={{
              backgroundColor: "var(--color-accent-light, #e4f8f8)",
              color: "var(--color-text, #056363)",
              border: "1px solid var(--color-border, #e2e8f0)",
              borderRadius: 6,
            }}
          >
            <strong>Total Quantity Recorded on this MB:</strong>
            <div className="h4 text-right mt-2 mb-0">{totalQuantity.toLocaleString()}</div>
          </div>
        </div>
      </div>
      <div className="float-right  mb-3">
        <ActionButton text="Cancel" variant="cancel" onClick={() => history.goBack()} />
        <ActionButton text="Save as Draft" variant="grey" onClick={() => save("Draft")} />
        <ActionButton
          text="Save & Submit MB"
          icon={BsCloudUpload}
          onClick={() => save("Submitted")}
        />
      </div>
    </div>
  );
};

export default MeasurementBookForm;
